import { Button, Input } from '@truecare/ui';
import { useEffect, useState, type ReactNode } from 'react';

/**
 * Phase 5f-2 — shared `<ArtifactRow>` for credentials + compliance catalog tables.
 *
 * Driven by a per-row data shape returned by the catalog endpoints
 * (5f-b: credentials catalog; 5f-c: compliance catalog). Adding a new
 * artifact type adds a row in the backend catalog response — no
 * portal code changes. Type-specific extras (WSIB account number,
 * future vaccination lot number, etc.) live in the `extraFields`
 * metadata + render below the upload button.
 *
 * Status semantics (from ArtifactReviewStatus):
 *   - null currentArtifact → "Not uploaded" + Upload button
 *   - PENDING              → "Awaiting review" + Re-upload button
 *   - APPROVED + within expiry → "Approved (expires YYYY-MM-DD)" + Re-upload button
 *   - APPROVED + past expiry / EXPIRED → "Expired YYYY-MM-DD" + Re-upload (warning)
 *   - REJECTED             → "Rejected" + Re-upload (action required)
 *
 * Upload mechanics (signed-URL → S3 → record) ship in 5f-b/5f-c; for
 * 5f-a this component renders the row structure + status + extras
 * slot. The upload click handler is a no-op placeholder until the
 * catalog wiring lands.
 */

export interface ArtifactRowData {
  type: string;
  label: string;
  requiredForCaregiver: boolean;
  currentArtifact: {
    id: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
    expiresAt: string | null;
    externalReferenceNumber: string | null;
    /** Audit H15 — operator's rejection reason (present only when REJECTED). */
    rejectionNotes: string | null;
  } | null;
  /** Per-type field metadata (e.g., ['externalReferenceNumber'] for WSIB).
   *  Renders inline below the action button. */
  extraFields: string[];
  /** Optional per-extra-field label override. Defaults sensibly. */
  extraFieldLabels?: Record<string, string>;
}

interface Props {
  data: ArtifactRowData;
  /** Click handler — receives the row's type + any extraFields the
   *  caregiver entered. The 5f-b/c implementations wire this to the
   *  signed-URL → S3 → record flow. Until then it's a no-op. */
  onUpload?: (input: { type: string; extras: Record<string, string> }) => void;
  /** View-only mode (audit F1): while the caregiver is UNDER_REVIEW /
   *  ONBOARDING their documents are frozen, so the upload action is hidden
   *  and the reference-number field is not editable — but the row still
   *  renders so /me isn't a blank shell during review. */
  readOnly?: boolean;
  /** Optional trailing slot for status badges, audit-trail links, etc. */
  trailing?: ReactNode;
}

const STATUS_PILL: Record<NonNullable<ArtifactRowData['currentArtifact']>['status'], string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  APPROVED: 'bg-green-50 text-green-700 border-green-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
  EXPIRED: 'bg-red-50 text-red-700 border-red-200',
};

function buildInitialExtras(
  extraFields: string[],
  currentArtifact: ArtifactRowData['currentArtifact'],
): Record<string, string> {
  const initial: Record<string, string> = {};
  for (const field of extraFields) {
    if (field === 'externalReferenceNumber') {
      initial[field] = currentArtifact?.externalReferenceNumber ?? '';
    } else {
      initial[field] = '';
    }
  }
  return initial;
}

function deriveDisplayStatus(
  current: ArtifactRowData['currentArtifact'],
  now: Date = new Date(),
): { label: string; tone: 'pending' | 'approved' | 'rejected' | 'expired' | 'missing' } {
  if (current === null) return { label: 'Not uploaded', tone: 'missing' };
  if (current.status === 'PENDING') return { label: 'Awaiting review', tone: 'pending' };
  if (current.status === 'REJECTED') return { label: 'Rejected', tone: 'rejected' };
  if (current.status === 'EXPIRED') {
    return {
      label: current.expiresAt ? `Expired ${current.expiresAt}` : 'Expired',
      tone: 'expired',
    };
  }
  // APPROVED — check expiry
  if (current.expiresAt !== null) {
    const exp = new Date(current.expiresAt);
    if (exp.getTime() <= now.getTime()) {
      return { label: `Expired ${current.expiresAt}`, tone: 'expired' };
    }
    return { label: `Approved · expires ${current.expiresAt}`, tone: 'approved' };
  }
  return { label: 'Approved', tone: 'approved' };
}

export function ArtifactRow({ data, onUpload, trailing, readOnly = false }: Props) {
  const status = deriveDisplayStatus(data.currentArtifact);
  const needsAction = status.tone === 'missing' || status.tone === 'rejected' || status.tone === 'expired';
  const buttonLabel = data.currentArtifact === null ? 'Upload' : 'Re-upload';

  // Local state for extra fields (e.g., WSIB account number). Pre-fill
  // from the current artifact if present (per 5f-G migration's
  // pre-fill semantics).
  //
  // Phase 5f audit r1 — extras must re-sync when the parent re-renders
  // with updated `data.currentArtifact.externalReferenceNumber` (which
  // will fire after upload + query invalidation in 5f-b/c). The
  // useState initializer ran ONCE on mount, leaving stale values
  // visible until row unmount. useEffect tied to the pre-fill source
  // keeps the input in sync without losing operator edits in flight
  // (operator-typed value persists between prop refreshes because
  // setExtras only fires when the pre-fill VALUE actually changes).
  const [extras, setExtras] = useState<Record<string, string>>(() =>
    buildInitialExtras(data.extraFields, data.currentArtifact),
  );
  const prefillExternalRef = data.currentArtifact?.externalReferenceNumber ?? '';
  useEffect(() => {
    setExtras((prev) => {
      const next = { ...prev };
      for (const field of data.extraFields) {
        if (field === 'externalReferenceNumber') {
          // Only overwrite if local is empty or matched the previous
          // pre-fill — protects operator-typed-but-unsubmitted edits.
          if (prev[field] === '' || prev[field] === undefined) {
            next[field] = prefillExternalRef;
          }
        } else if (!(field in prev)) {
          next[field] = '';
        }
      }
      return next;
    });
  }, [prefillExternalRef, data.extraFields]);

  const pillClass =
    data.currentArtifact === null
      ? 'bg-ink/5 text-ink-muted border-border'
      : STATUS_PILL[data.currentArtifact.status];

  const defaultExtraLabel = (key: string): string => {
    if (key === 'externalReferenceNumber') {
      // VSCs + NACC certificates carry certificate numbers, not account
      // numbers; WSIB clearance is an account number. Everything else
      // gets the cleaner generic.
      if (data.type === 'VSC' || data.type === 'NACC' || data.type === 'DSW_CREDENTIAL')
        return 'Certificate number';
      if (data.type === 'WSIB_CLEARANCE') return 'Account number';
      return 'Certificate / account number';
    }
    return key;
  };

  return (
    <div
      className="flex flex-col gap-3 border-b border-border py-3 last:border-b-0"
      data-testid={`artifact-row-${data.type}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-body text-sm font-medium text-forest">
            {data.label}
            {data.requiredForCaregiver ? (
              <span className="ml-2 text-xs font-normal text-ink-muted">required</span>
            ) : (
              <span className="ml-2 text-xs font-normal text-ink-muted">optional</span>
            )}
          </div>
          <div className="mt-1">
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${pillClass}`}
            >
              {status.label}
            </span>
          </div>
          {/* Audit H15 — show the operator's rejection reason so the caregiver
              knows what to fix before re-uploading (mirrors the banking
              eftRejectionReason banner). */}
          {data.currentArtifact?.status === 'REJECTED' && data.currentArtifact.rejectionNotes && (
            <p
              className="mt-1 rounded-md bg-red-50 px-2 py-1 text-xs text-red-700"
              data-testid={`artifact-row-${data.type}-rejection-reason`}
            >
              <span className="font-medium">Reason:</span> {data.currentArtifact.rejectionNotes}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {trailing}
          {!readOnly && (
            <Button
              type="button"
              variant={needsAction ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => onUpload?.({ type: data.type, extras })}
              disabled={!onUpload}
              data-testid={`artifact-row-${data.type}-action`}
            >
              {buttonLabel}
            </Button>
          )}
        </div>
      </div>

      {data.extraFields.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {data.extraFields.map((field) => {
            const fieldId = `artifact-row-${data.type}-extra-${field}`;
            return (
              <div key={field} className="flex flex-col gap-1">
                <label
                  htmlFor={fieldId}
                  className="text-xs uppercase tracking-wide text-ink-muted"
                >
                  {data.extraFieldLabels?.[field] ?? defaultExtraLabel(field)}
                </label>
                <Input
                  id={fieldId}
                  type="text"
                  value={extras[field] ?? ''}
                  onChange={(e) =>
                    setExtras((prev) => ({ ...prev, [field]: e.target.value }))
                  }
                  disabled={readOnly}
                  data-testid={fieldId}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
